import React, { useCallback, useEffect, useState } from 'react';
import { api, type RetentionLogEntry, type RetentionRule, type RetentionRunResult, type RetentionStatus } from '../api';
import { TYPE, useTheme } from '../theme';
import { Badge, Button, Card, ErrorState, Input, Loader, Switch } from '../components/ui';

// GA3 · Admin → Gallring: GDPR-gallringens regler (API: services/retention.ts). Nya regler står i
// förhandsvisning och raderar inget; admin slår på skarp radering per regel. Servern kontrollerar
// admin, 7-årslåset och spärren — den här vyn bara visar och frågar.

const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' }) : '–');
const nr = (n: number) => n.toLocaleString('sv-SE');
const BASIS: Record<RetentionRule['basis'], string> = { lag: 'Lag', avtal: 'Avtal', berattigat_intresse: 'Berättigat intresse' };
const ACTION: Record<RetentionRule['action'], string> = { delete: 'Raderas', anonymize: 'Avidentifieras', notify: 'Bara avisering' };
const STATUS: Record<RetentionLogEntry['status'], string> = { ok: 'Klar', blocked: 'Spärrad', error: 'Fel', disabled: 'Avstängd' };
const TRIGGER: Record<RetentionLogEntry['trigger'], string> = { nightly: 'Natt', manual: 'Kör nu', preview: 'Förhandsvisning' };

export function daysText(d: number) {
  if (d % 365 === 0 || d === 2922) return d === 2922 ? '8 år' : `${d / 365} år`;
  if (d >= 60 && d % 30 === 0) return `${d / 30} mån`;
  return d === 1 ? '1 dygn' : `${d} dygn`;
}

function resultText(r: RetentionRunResult) {
  if (r.status === 'disabled') return 'Gallringen är avstängd (RETENTION=off).';
  if (r.status === 'error') return `Fel: ${r.message ?? 'okänt'}`;
  if (r.status === 'blocked') return r.message ?? 'Spärrad.';
  if (r.mode === 'preview') return `Förhandsvisning: ${nr(r.matched)} poster skulle gallras. Inget har ändrats.`;
  return `${nr(r.affected)} poster gallrade${r.files ? `, ${nr(r.files)} filer borttagna${r.filesFailed ? ` (${r.filesFailed} misslyckades)` : ''}` : ''}.`;
}

export function RetentionPanel() {
  const T = useTheme();
  const [status, setStatus] = useState<RetentionStatus | null>(null);
  const [log, setLog] = useState<RetentionLogEntry[] | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [days, setDays] = useState<Record<string, string>>({});
  const [results, setResults] = useState<Record<string, string>>({});

  const load = useCallback(() => {
    setError('');
    api.retentionStatus().then(setStatus).catch(e => setError(e.message || String(e)));
    api.retentionLog(null, 50).then(setLog).catch(() => setLog([]));
  }, []);
  useEffect(load, [load]);

  const replace = (rule: RetentionRule) => setStatus(s => (s ? { ...s, rules: s.rules.map(r => (r.key === rule.key ? rule : r)) } : s));

  async function update(r: RetentionRule, patch: { days?: number; enabled?: boolean; mode?: 'preview' | 'live' }) {
    setBusy(r.key);
    try { replace(await api.updateRetentionRule(r.key, patch)); setDays(d => ({ ...d, [r.key]: '' })); }
    catch (e: any) { alert(e.message || String(e)); }
    finally { setBusy(null); }
  }

  function toggleLive(r: RetentionRule, live: boolean) {
    if (live && !confirm(`Slå på skarp radering för "${r.label}"?\n\nVarje natt ${r.action === 'anonymize' ? 'avidentifieras' : 'raderas'} poster äldre än ${daysText(r.days)}. Just nu gäller det ${nr(r.pending ?? 0)} poster. Det går inte att ångra.`)) return;
    void update(r, { mode: live ? 'live' : 'preview' });
  }

  async function run(r: RetentionRule, dryRun: boolean) {
    if (!dryRun && r.mode === 'live' && !confirm(`Kör "${r.label}" skarpt nu?\n\n${nr(r.pending ?? 0)} poster äldre än ${daysText(r.days)} ${r.action === 'anonymize' ? 'avidentifieras' : 'raderas'}. Det går inte att ångra.`)) return;
    setBusy(r.key);
    try {
      let res = await api.runRetentionRule(r.key, dryRun);
      if (res.status === 'blocked' && confirm(`${res.message}\n\nKör ändå och gallra ${nr(res.matched)} poster? Det går inte att ångra.`)) {
        res = await api.runRetentionRule(r.key, false, true);
      }
      setResults(m => ({ ...m, [r.key]: resultText(res) }));
      load();
    } catch (e: any) { setResults(m => ({ ...m, [r.key]: `Fel: ${e.message || String(e)}` })); }
    finally { setBusy(null); }
  }

  if (error) return <ErrorState message={error} />;
  if (!status) return <Loader text="Hämtar gallringsregler…" />;

  return (
    <div>
      <p style={{ ...TYPE.small, color: T.textSub, margin: '0 0 14px' }}>
        Gallringen körs varje natt från kl. 03. Regler i förhandsvisning räknar bara och loggar; inget raderas förrän du slår på skarp radering.
        En körning som skulle ta fler än {nr(status.maxRows)} poster eller mer än {status.maxSharePct} % av en tabell stoppas tills du kör den här och bekräftar.
        Bokförings- och betalningsunderlag kan aldrig gallras före 8 år (bokföringslagen).
      </p>
      {status.disabled && (
        <Card padding={14} style={{ marginBottom: 12, background: T.dangerSoft }}>
          <span style={{ color: T.danger, fontWeight: 700 }}>Gallringen är avstängd i servern (RETENTION=off).</span>
          <span style={{ ...TYPE.small, color: T.textSub }}> Inga körningar sker, varken nattliga eller manuella.</span>
        </Card>
      )}

      <div style={{ display: 'grid', gap: 12 }}>
        {status.rules.map(r => {
          const live = r.mode === 'live';
          const working = busy === r.key;
          const draft = days[r.key] ?? '';
          const draftDays = Number(draft);
          return (
            <Card key={r.key} padding={16}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
                <span style={{ color: T.text, fontWeight: 700, flex: '1 1 220px', minWidth: 0 }}>{r.label}</span>
                {!r.enabled ? <Badge>Av</Badge> : r.action === 'notify' ? <Badge tone="info">Avisering</Badge> : live ? <Badge tone="danger" dot>Skarp</Badge> : <Badge tone="accent">Förhandsvisning</Badge>}
                {r.locked && <Badge tone="info">7-årslås</Badge>}
              </div>
              <div style={{ ...TYPE.small, color: T.textSub, marginBottom: 6 }}>{r.description}</div>
              <div style={{ ...TYPE.small, color: T.textMuted, marginBottom: 10 }}>
                {daysText(r.days)} · {ACTION[r.action]} · Grund: {BASIS[r.basis]}{r.days !== r.defaultDays ? ` · förslag ${daysText(r.defaultDays)}` : ''} — {r.why}
              </div>

              <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
                <span style={{ color: T.text, fontSize: 15 }}>
                  {r.pendingError ? <span style={{ color: T.danger }}>Kunde inte räkna: {r.pendingError}</span>
                    : <><strong>{nr(r.pending ?? 0)}</strong> {r.action === 'notify' ? 'att avisera' : 'skulle gallras nu'}</>}
                </span>
                <span style={{ ...TYPE.small, color: T.textMuted }}>Senast körd {fmt(r.lastRunAt)}{r.lastStatus ? ` (${STATUS[r.lastStatus as RetentionLogEntry['status']] ?? r.lastStatus}${r.lastCount != null ? `, ${nr(r.lastCount)}` : ''})` : ''}</span>
              </div>
              {r.blockReason && live && (
                <div style={{ ...TYPE.small, color: T.danger, marginBottom: 10 }}>Spärren stoppar nattens körning: {r.blockReason}. Kör nu och bekräfta om det stämmer.</div>
              )}

              <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                <Switch label="På" checked={r.enabled} disabled={working} onChange={v => void update(r, { enabled: v })} />
                {r.action !== 'notify' && <Switch label="Skarp radering" checked={live} disabled={working || !r.enabled} onChange={v => toggleLive(r, v)} />}
                <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                  <Input aria-label={`Lagringstid i dygn för ${r.label}`} type="number" inputMode="numeric" min={r.minDays} placeholder={String(r.days)}
                    value={draft} onChange={e => setDays(d => ({ ...d, [r.key]: e.target.value }))} style={{ width: 96 }} />
                  <span style={{ ...TYPE.small, color: T.textMuted }}>dygn</span>
                  <Button size="sm" variant="secondary" disabled={working || !draft || !(draftDays >= r.minDays) || draftDays === r.days}
                    onClick={() => void update(r, { days: draftDays })}>Spara</Button>
                </span>
                <span style={{ flex: '1 1 0' }} />
                <Button size="sm" variant="secondary" disabled={working || status.disabled} onClick={() => void run(r, true)}>Förhandsvisa</Button>
                <Button size="sm" variant={live ? 'danger' : 'secondary'} loading={working} disabled={working || status.disabled || !r.enabled} onClick={() => void run(r, false)}>Kör nu</Button>
              </div>
              {r.locked && <div style={{ ...TYPE.small, color: T.textMuted, marginTop: 8 }}>Kortast {daysText(r.minDays)}.</div>}
              {results[r.key] && <div role="status" style={{ ...TYPE.small, color: T.text, marginTop: 10 }}>{results[r.key]}</div>}
            </Card>
          );
        })}
      </div>

      <h2 style={{ color: T.text, fontSize: 15, fontWeight: 700, margin: '24px 0 10px' }}>Gallringslogg</h2>
      {log === null ? <Loader text="Hämtar loggen…" /> : log.length === 0 ? (
        <div style={{ ...TYPE.small, color: T.textMuted }}>Inga körningar än.</div>
      ) : (
        <Card padding={0}>
          {log.map((l, i) => (
            <div key={l.id} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'baseline', padding: '10px 14px', borderTop: i ? `1px solid ${T.border}` : 'none' }}>
              <span style={{ ...TYPE.small, color: T.textMuted, minWidth: 110 }}>{fmt(l.startedAt)}</span>
              <span style={{ color: T.text, fontWeight: 600, flex: '1 1 180px', minWidth: 0 }}>{l.label}</span>
              <span style={{ ...TYPE.small, color: T.textSub }}>{TRIGGER[l.trigger] ?? l.trigger} · {l.mode === 'live' ? 'skarp' : 'torrkörning'}</span>
              <span style={{ ...TYPE.small, color: l.status === 'ok' ? T.text : T.danger }}>
                {STATUS[l.status] ?? l.status} · {l.mode === 'live' && l.status === 'ok' ? `${nr(l.affected)} gallrade` : `${nr(l.matched)} träffar`}
                {l.files ? ` · ${nr(l.files)} filer` : ''}{l.actor ? ` · ${l.actor}` : ''}
              </span>
              {l.error && <div style={{ ...TYPE.small, color: T.danger, flexBasis: '100%' }}>{l.error}</div>}
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}
